import React, { useContext, useEffect } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { GlobalStyles } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CheckoutContext, CheckoutContextProps } from '../context/CheckoutContext';
import { useDfcLocale } from '../context/DfcLocaleContext';
import { BaseUrlToCompany, DUBAI_FITNESS_CHALLENGE_COMPANY_ID } from '../Utils/constants';
import { DfcDirection, DfcLocale } from '../Utils/dfcLocale';
import { isAsaCompany } from '../Utils/asaBrand';

/**
 * Right-to-left for the DFC Fitness Village in Arabic.
 *
 * The village's styles are written left-to-right. Under `rtlEmotionCache` the RTL stylis plugin
 * mirrors every physical property as the CSS is generated (margin-left → margin-right, left: 0 →
 * right: 0, translateX(8px) → translateX(-8px)), so components need no RTL branches of their own.
 * What it cannot see still needs one: icons that point somewhere (`[dir='rtl'] & { transform:
 * scaleX(-1) }` — scale is left alone) and positions computed in JS or set with inline `style`.
 *
 * `ltrEmotionCache` is emotion's default cache spelled out (same `css` key, same prefixer), so
 * every other company renders exactly as it did before the provider existed.
 */
const ltrEmotionCache = createCache({ key: 'css', stylisPlugins: [prefixer] });

const rtlEmotionCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

/** Dubai — the Dubai Government typeface, bundled in `src/fonts` — carries the Arabic copy. */
const DFC_ARABIC_FONT_FAMILY = "Dubai, system-ui, 'Segoe UI', Tahoma, sans-serif";

/**
 * The page's language and direction: the stored choice applies only when the company uses the
 * village theme; every other company stays English, left-to-right.
 *
 * Until the company has loaded its theme is unknown, so the DFC company id in the URL (or the
 * DFC domain) stands in for it — the same test the home page uses to render the village home
 * before the company arrives — and an Arabic visitor never sees the English hero flash first.
 */
export const useDfcPageDirection = (): { locale: DfcLocale; direction: DfcDirection } => {
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const { locale, direction } = useDfcLocale();
  const [searchParams] = useSearchParams();

  const isDfcVillage = company
    ? company.theme === 'dfcVillageTheme'
    : (searchParams.get('companyId') || BaseUrlToCompany.get(window.location.origin)) ===
      DUBAI_FITNESS_CHALLENGE_COMPANY_ID;

  // All Sports Arena borrows the village theme but not DFC's Arabic: always English.
  if (!isDfcVillage || isAsaCompany(company)) return { locale: 'en', direction: 'ltr' };
  return { locale, direction };
};

/**
 * Arabic typography for the whole page, portals included. Letter-spacing breaks the joins
 * between Arabic letters and the Dubai typeface has no italic, so both are switched off; the
 * village's own roots name their font explicitly, hence the universal selector.
 */
const arabicGlobalStyles = (
  <GlobalStyles
    styles={{
      'html[lang="ar"] body, html[lang="ar"] body *': {
        fontFamily: `${DFC_ARABIC_FONT_FAMILY} !important`,
        letterSpacing: 'normal !important',
        fontStyle: 'normal !important',
      },
    }}
  />
);

/**
 * Sits inside `CheckoutContextProvider` (it needs the company) and around `App`: sets the
 * document's `lang` / `dir` — which portals (dialogs, drawers, toasts) inherit too — and swaps
 * the emotion cache. `App` passes the same direction to the MUI theme.
 */
export const DfcDirectionProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { locale, direction } = useDfcPageDirection();

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = direction;
  }, [locale, direction]);

  return (
    <CacheProvider value={direction === 'rtl' ? rtlEmotionCache : ltrEmotionCache}>
      {locale === 'ar' && arabicGlobalStyles}
      {children}
    </CacheProvider>
  );
};
